'use client'
import { motion } from 'framer-motion'
import { useLang } from '../../context/LangContext'
import { t, productSecurity, toLocalDigits } from '../../lib/translations'
import ProductLanding from '../../components/ProductLanding'
import Icon from '../../components/Icon'

const featureIcons = ['shield-check', 'lightning', 'clipboard', 'globe', 'server', 'bell', 'monitor', 'users'] as const
const serviceIcons = ['shield', 'building', 'code', 'apps', 'transfer', 'users'] as const

export default function SecurityPage() {
  const lang = useLang()
  const features = productSecurity.features.map((f, i) => ({
    title: t(lang, f.title),
    description: t(lang, f.description),
    icon: <Icon name={featureIcons[i]} className="w-6 h-6" strokeWidth={1.5} />,
  }))
  const benefits = productSecurity.benefits.map((b) => ({ title: t(lang, b.title), desc: t(lang, b.desc) }))

  return (
    <ProductLanding
      id="security"
      title={t(lang, productSecurity.title)}
      subtitle={t(lang, productSecurity.subtitle)}
      description={t(lang, productSecurity.description)}
      heroBg="bg-gradient-to-b from-[#4338ca]/5 via-transparent to-transparent"
      heroGlow="bg-[#4f46e5]/10"
      features={features}
      benefits={benefits}
    >
      <section className="relative py-24 sm:py-32" id="pen-test">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto"
          >
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div className="space-y-6">
                <span className="text-sm font-medium text-[#818cf8] tracking-wide uppercase">{t(lang, productSecurity.penTestKicker)}</span>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">
                  {t(lang, productSecurity.penTestTitle)}{' '}
                  <span className="gradient-text">{t(lang, productSecurity.penTestHighlight)}</span>
                </h2>
                <p className="text-[#94a3b8] leading-relaxed">
                  {t(lang, productSecurity.penTestDesc)}
                </p>
                <ul className="space-y-3">
                  {productSecurity.penTestBullets.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#cbd5e1]">
                      <Icon name="check" className="w-4 h-4 text-[#22c55e] mt-0.5 shrink-0" />
                      {t(lang, item)}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#4338ca]/10 via-[#4f46e5]/5 to-transparent rounded-3xl blur-3xl pointer-events-none" />
                <div className="relative rounded-3xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm p-8 sm:p-10 text-center">
                  <p className="text-sm text-[#94a3b8] mb-2">{t(lang, productSecurity.startingFrom)}</p>
                  <div className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-1">
                    {toLocalDigits('80,000,000', lang)}
                  </div>
                  <div className="text-2xl font-semibold text-[#818cf8] mb-2">{t(lang, productSecurity.toman)}</div>
                  <p className="text-sm text-[#64748b] mb-8">
                    {t(lang, productSecurity.perEngagement)}
                  </p>
                  <a
                    href="tel:+989920115977"
                    className="group inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] font-semibold text-sm hover:from-[#6366f1] hover:to-[#c084fc] transition-all duration-200 shadow-2xl shadow-[#4f46e5]/25 glow btn-lightning"
                  >
                    <Icon name="phone" className="w-4 h-4" />
                    {t(lang, productSecurity.callForHelp)}
                  </a>
                  <p className="mt-4 text-xs text-[#475569]">
                    {t(lang, productSecurity.speakWithTeam)}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative py-24 sm:py-32">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium text-[#818cf8] tracking-wide uppercase">{t(lang, productSecurity.servicesKicker)}</span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              {t(lang, productSecurity.servicesHeading)}{' '}
              <span className="gradient-text">{t(lang, productSecurity.servicesHighlight)}</span>
            </h2>
            <p className="mt-4 text-lg text-[#94a3b8] max-w-2xl mx-auto">
              {t(lang, productSecurity.servicesDesc)}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {productSecurity.services.map((service, i) => (
              <motion.div
                key={t(lang, service.title)}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group p-6 rounded-xl border border-white/[0.04] bg-white/[0.02] hover:bg-white/[0.05] hover:border-white/[0.08] transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-[#4f46e5]/10 flex items-center justify-center mb-4 group-hover:bg-[#4f46e5]/20 transition-colors duration-300">
                  <Icon name={serviceIcons[i]} className="w-5 h-5 text-[#818cf8]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{t(lang, service.title)}</h3>
                <p className="text-sm text-[#94a3b8] leading-relaxed">{t(lang, service.desc)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative py-24 sm:py-32">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#4338ca]/[0.03] via-transparent to-[#4f46e5]/[0.03] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-8 sm:px-8 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative rounded-3xl p-10 sm:p-16 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#1e1b4b]/80 via-[#2e1065]/60 to-[#07070d] rounded-3xl border border-[#4f46e5]/20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#4f46e5]/10 via-transparent to-[#7c3aed]/10 rounded-3xl" />

            <div className="relative">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight flex flex-col gap-4 items-center">
                <span>{t(lang, productSecurity.ctaTitle)}</span>
                <span className="gradient-text">{t(lang, productSecurity.ctaHighlight)}</span>
              </h2>
              <p className="mt-5 text-lg text-[#94a3b8] max-w-xl mx-auto">
                {t(lang, productSecurity.ctaDesc)}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="tel:+989920115977"
                  className="group relative px-8 py-3.5 rounded-xl bg-gradient-to-r from-[#4f46e5] to-[#7c3aed] font-semibold text-sm hover:from-[#6366f1] hover:to-[#c084fc] transition-all duration-200 shadow-2xl shadow-[#4f46e5]/25 glow btn-lightning"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    <Icon name="phone" className="w-4 h-4" />
                    {t(lang, productSecurity.callForHelp)}
                  </span>
                </a>
                <a
                  href="#cta"
                  className="px-8 py-3.5 rounded-xl glass glass-hover font-medium text-sm text-[#e2e8f0] transition-all duration-300"
                >
                  {t(lang, productSecurity.submitRequest)}
                </a>
              </div>
              <p className="mt-6 text-xs text-[#475569]">
                {t(lang, productSecurity.responseTime)}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </ProductLanding>
  )
}
